import { fetchRetry } from '../gateway/fetch';
import { logError } from './logging';
import { supportsWebP } from './webpDetection';

const imageType = supportsWebP() ? 'image/webp' : 'image/jpeg';

/**
 * Fetches an image from a URL and converts it to a File object.
 * @param {string} imageUrl - The URL of the image to fetch.
 * @param {string} filename - The filename for the File object.
 * @returns {Promise<File>} A Promise that resolves to a File object.
 * @throws {Error} If the image fetch fails or the response is not valid.
 */
export async function fetchImageAsFile(imageUrl, filename = `featured-image.${imageType.split('/').at(1)}`) {
  // If the url is wrapped in quotes, remove them.
  if (imageUrl.startsWith('"') && imageUrl.endsWith('"')) {
    imageUrl = imageUrl.slice(1, -1);
  }
  try {
    const response = await fetchRetry({
      retries: 5,
      retryDelay: 500,
      url: imageUrl,
      options: {
        method: 'GET',
        headers: {
          'Content-Type': 'binary/octet-stream'
        }
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();

    // Create a File object from the blob.
    return new File([blob], filename, { type: blob.type || imageType });
  } catch (error) {
    logError({
      label: 'fetchImageAsFile',
      error,
      message: `Failed to fetch image from URL: ${imageUrl}`,
      extraData: { imageUrl, filename },
      throwError: true
    });
    // This line will never be reached due to throwError: true, but satisfies TypeScript.
    throw error;
  }
}

/**
 * @param {object} props
 * @param {FormData} props.formData
 * @param {Array} props.items - Array of cart items to check for _featured_image URLs.
 * @param {{url: string, file: File | null, visualiserSlotMode: string, groupId: string, lineItemPropertyKey: string, label: string}[]} props.visData - Array of visualiser data.
 * @returns {Promise<void>} A Promise that resolves when all images are processed.
 */
export const addImagesToFormData = async ({ formData, items = [], visData = [] }) => {
  // First, handle Files that are directly in item properties (e.g., from visualiser IMAGE_UPLOAD slots).
  items.forEach((item, itemIndex) => {
    if (item.properties && typeof item.properties === 'object') {
      Object.entries(item.properties).forEach(([key, value]) => {
        if (value instanceof File) {
          // This File was stored with its lineItemPropertyKey as the property name.
          // Add it to FormData with the correct key.
          formData.append(`items[${itemIndex}][properties][${key}]`, value, value.name);
        }
      });
    }
  });

  // Handle _featured_image URLs by fetching and converting to File objects.
  const featuredImagePromises = items.map(async (item, itemIndex) => {
    if (
      item.properties &&
      Object.keys(item.properties).some((key) => {
        if (!key.startsWith('_featured_image')) return false;
        const value = item.properties[key];
        return typeof value === 'string' && value.includes('http');
      })
    ) {
      // Find all _featured_image variants and process them.
      const featuredImageKeys = Object.keys(item.properties).filter((key) => key.startsWith('_featured_image'));

      for (const key of featuredImageKeys) {
        const imageUrl = item.properties[key];

        // Check if we already have a file for this key in the visData.
        const visDataFile = visData.find((vis) => vis.groupId === item.groupId);
        if (visDataFile && visDataFile.file) {
          formData.delete(`items[${itemIndex}][properties][${key}]`);
          formData.append(`items[${itemIndex}][properties][${key}]`, visDataFile.file, visDataFile.file.name);
          console.log('Added visData file for key', key, visDataFile.file.name);
          continue;
        }

        if (typeof imageUrl === 'string' && imageUrl.includes('http')) {
          try {
            // @TODO: re-enable when we figure out how to get the images uploaded quick enough.
            const imageFile = await fetchImageAsFile(
              imageUrl,
              `featured-image-${itemIndex}.${imageType.split('/').at(1)}`
            );
            // Remove the old value from the form data.
            formData.delete(`items[${itemIndex}][properties][${key}]`);
            formData.append(`items[${itemIndex}][properties][${key}]`, imageFile, imageFile.name);
            // formData.append(`items[${itemIndex}][properties][_personalisation_base_image]`, imageFile, imageFile.name);
            // formData.append(`items[${itemIndex}][properties][_personalisation_base_image]`, imageUrl);
          } catch (error) {
            console.log('error', error);
            logError({
              label: 'addImagesToFormData',
              error,
              message: `Failed to fetch featured image for item ${itemIndex}`,
              extraData: { itemIndex, imageUrl, propertyKey: key }
            });
            // Fallback to URL string if fetch fails.
            formData.append(`items[${itemIndex}][properties][${key}]`, imageUrl);
          }
        }
      }
    }
  });

  // Wait for all featured image fetches to complete.
  await Promise.all(featuredImagePromises);
};
